import React, { useState } from 'react';
import {
  BodyText,
  Box,
  Button,
  Column,
  FormField,
  Input,
  Row,
  TagInput,
} from '@chargify/custom-ui-components';
import { useTranslation } from 'react-i18next';

import { emailRegex } from '../../constants';
import { ContactDetailsModel } from '../../models/customerDetails';
import { CodeBlock, InputHint } from './ContactDetailsForm.styles';

export type ContactDetailsFormProps = {
  values: ContactDetailsModel;
  onSubmit: (values: ContactDetailsModel) => void;
  onCancel: () => void;
  isSaving: boolean;
};
const ContactDetailsForm = ({
  values,
  onCancel,
  onSubmit,
  isSaving,
}: ContactDetailsFormProps) => {
  const { t } = useTranslation('customer-details');
  const [formValue, setFormValue] = useState(values);
  const handleChange = (name: string, value: string | string[]) => {
    setFormValue((prev) => ({ ...prev, [name]: value }));
  };
  return (
    <Box>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formValue);
        }}
      >
        <Row>
          <Column span={24}>
            <FormField
              label={t('contact_details.organization_name')}
              name={'organization'}
              fullWidth
            >
              <Input
                fullWidth
                value={formValue.organization}
                placeholder={t('contact_details.organization_name_placeholder')}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                name={'organization'}
                id={'organization'}
              />
            </FormField>
          </Column>
          <Column span={[24, 12]}>
            <FormField
              fullWidth
              label={t('contact_details.first_name')}
              name={'firstName'}
            >
              <Input
                fullWidth
                placeholder={t('contact_details.first_name_placeholder')}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                value={formValue.firstName}
                name={'firstName'}
                id={'firstName'}
              />
            </FormField>
          </Column>
          <Column span={[24, 12]}>
            <FormField
              label={t('contact_details.last_name')}
              name={'lastName'}
              fullWidth
            >
              <Input
                fullWidth
                placeholder={t('contact_details.last_name_placeholder')}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                name={'lastName'}
                id={'lastName'}
                value={formValue.lastName}
              />
            </FormField>
          </Column>
          <Column span={[24, 12]}>
            <FormField
              label={t('contact_details.email')}
              name={'email'}
              fullWidth
            >
              <Input
                fullWidth
                placeholder={t('contact_details.email_placeholder')}
                type={'email'}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                value={formValue.email}
                name={'email'}
                id={'email'}
              />
            </FormField>
          </Column>
          <Column span={[24, 12]}>
            <FormField
              label={t('contact_details.phone')}
              name={'phoneNumber'}
              fullWidth
            >
              <Input
                fullWidth
                placeholder={t('contact_details.phone_placeholder')}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                name={'phoneNumber'}
                id={'phoneNumber'}
                value={formValue.phoneNumber}
              />
            </FormField>
          </Column>
          <Column span={24}>
            <FormField
              disableMessage
              label={t('contact_details.emails_cc')}
              name={'ccEmails'}
              fullWidth
            >
              <TagInput
                maxTags={5}
                fullWidth
                id={'ccEmails'}
                name={'ccEmails'}
                tags={formValue.ccEmails}
                placeholder={t('contact_details.emails_cc_placeholder')}
                onPaste={(v) =>
                  setFormValue((prev) => ({
                    ...prev,
                    ccEmails: [...prev.ccEmails, ...v],
                  }))
                }
                onCreate={(v) =>
                  setFormValue((prev) => ({
                    ...prev,
                    ccEmails: [...prev.ccEmails, v],
                  }))
                }
                onRemove={(v, i) =>
                  setFormValue((prev) => ({
                    ...prev,
                    ccEmails: prev.ccEmails.filter((_, index) => index !== i),
                  }))
                }
                validationFn={(v: string) => emailRegex.test(v)}
              />
            </FormField>
            <InputHint marginTop={2}>
              <BodyText variant={'body3'} fontWeight={600} color={'gray.dark'}>
                {t('contact_details.emails_cc_hint_2')}
                <CodeBlock
                  backgroundColor={'white.dark'}
                  marginLeft={1}
                  padding={1}
                >
                  {','}
                </CodeBlock>
              </BodyText>
              <BodyText variant={'body3'} fontWeight={400} color={'gray.dark'}>
                {t('contact_details.emails_cc_hint')}
              </BodyText>
            </InputHint>
          </Column>
          <Column span={24}>
            <FormField
              fullWidth
              label={t('contact_details.vat_number')}
              name={'vatNumber'}
            >
              <Input
                fullWidth
                placeholder={t('contact_details.vat_number_placeholder')}
                id={'vatNumber'}
                name={'vatNumber'}
                value={formValue.vatNumber}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                type={'text'}
              />
            </FormField>
          </Column>
          <Column span={24}>
            <Button.Group>
              <Button
                label={t('contact_details.update_contact_details')}
                color={'primary'}
                isLoading={isSaving}
              />
              <Button
                label={t('cancel', {
                  ns: 'common',
                })}
                color={'secondary'}
                onClick={onCancel}
              />
            </Button.Group>
          </Column>
        </Row>
      </form>
    </Box>
  );
};
export default ContactDetailsForm;
